'use client';

import React, { useEffect, useState } from 'react';
import PublicHero from '@/app/components/PublicHero';
import LandingFooter from '@/app/components/LandingFooter';
import BookingWidget from '@/app/components/BookingWidget';
import { createClient } from '@/lib/supabase/client';
import { CheckCircle, Clock, Globe, Video } from 'lucide-react';
import { useTranslation } from '@/i18n';

export default function BookDemoPage() {
  const { t, locale } = useTranslation();
  const [pageTitle, setPageTitle] = useState(t('publicWebsite:bookDemo.heroTitle') || (locale === 'ar' ? 'احجز عرضاً توضيحياً' : 'Book a Demo'));
  const [pageDescription, setPageDescription] = useState(t('publicWebsite:bookDemo.heroDescription') || (locale === 'ar' ? 'اختر الوقت الذي يناسبك.' : 'Pick a time that works for you.'));

  useEffect(() => {
    (async () => {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('booking_settings')
          .select('page_title, page_description')
          .limit(1)
          .maybeSingle();
        const title = String((data as any)?.page_title || '').trim();
        const desc = String((data as any)?.page_description || '').trim();
        if (title) setPageTitle(title);
        if (desc) setPageDescription(desc);
      } catch {
        // ignore
      }
    })();
  }, [t, locale]);

  const benefits = [
    {
      icon: Clock,
      title: t('publicWebsite:bookDemo.benefits.walkthrough.title') || (locale === 'ar' ? 'جولة عرض منتج لمدة 30 دقيقة' : '30-minute product walkthrough'),
      subtitle: t('publicWebsite:bookDemo.benefits.walkthrough.subtitle') || (locale === 'ar' ? 'نظرة إرشادية موجهة للمنصة.' : 'A guided overview of the platform.'),
    },
    {
      icon: CheckCircle,
      title: t('publicWebsite:bookDemo.benefits.uaeGuidance.title') || (locale === 'ar' ? 'إرشادات سير عمل PRO الإماراتي' : 'UAE PRO workflow guidance'),
      subtitle: t('publicWebsite:bookDemo.benefits.uaeGuidance.subtitle') || (locale === 'ar' ? 'أفضل الممارسات للتراخيص والتأشيرات والتجديدات.' : 'Best practices for licences, visas, renewals.'),
    },
    {
      icon: Globe,
      title: t('publicWebsite:bookDemo.benefits.moduleOverview.title') || (locale === 'ar' ? 'نظرة شاملة على الوحدات' : 'Full module overview'),
      subtitle: t('publicWebsite:bookDemo.benefits.moduleOverview.subtitle') || (locale === 'ar' ? 'المستندات والفواتير وسير عمل بوابة العملاء.' : 'Documents, invoices, and client portal flow.'),
    },
  ];

  const sidebarBullets = [
    t('publicWebsite:bookDemo.sidebar.bullet1') || (locale === 'ar' ? 'تعرف على كيفية إدارة بروفلو للشركات والأفراد وعملاء العقارات' : 'See how ProFlow manages companies, individuals, and property clients'),
    t('publicWebsite:bookDemo.sidebar.bullet2') || (locale === 'ar' ? 'تعرف على سير عمل تتبع التجديدات والمستندات' : 'Learn renewal/document tracking workflows'),
    t('publicWebsite:bookDemo.sidebar.bullet3') || (locale === 'ar' ? 'راجع الفواتير والمدفوعات وسير عمل بوابة العملاء' : 'Review invoices, payments, and client portal flow'),
    t('publicWebsite:bookDemo.sidebar.bullet4') || (locale === 'ar' ? 'اطرح أسئلة تخص نشاطك PRO الخاص' : 'Ask questions specific to your PRO business'),
  ];

  return (
    <div className="min-h-screen bg-background">
      <PublicHero
        title={pageTitle}
        subtitle={pageDescription}
        crumbs={[{ label: t('publicWebsite:bookDemo.crumbsHome') || (locale === 'ar' ? 'الرئيسية' : 'Home'), href: '/' }, { label: t('publicWebsite:bookDemo.crumbsBookDemo') || (locale === 'ar' ? 'احجز عرضاً توضيحياً' : 'Book Demo') }]}
      />

      <main className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 pb-14">
        <div className="-mt-10 sm:-mt-14 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {benefits.map((item) => (
              <div key={item.title} className="card-base p-5 border border-border bg-card shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-foreground">{item.title}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">{item.subtitle}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2">
            <BookingWidget audience="public" source="public_website" />
          </div>

          <aside className="lg:col-span-1">
            <div className="card-base p-6 border border-border bg-card shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center flex-shrink-0">
                    <Video className="w-5 h-5 text-accent" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-base font-bold text-foreground">{t('publicWebsite:bookDemo.sidebar.title') || (locale === 'ar' ? 'ما ستحصل عليه في العرض التوضيحي' : "What you'll get in the demo")}</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t('publicWebsite:bookDemo.sidebar.subtitle') || (locale === 'ar' ? 'جولة إرشادية مركزة مصممة لشركات خدمات PRO الإماراتية.' : 'A focused walkthrough tailored for UAE PRO service companies.')}
                    </p>
                  </div>
                </div>

                <ul className="mt-5 space-y-3 text-sm">
                  {sidebarBullets.map((text) => (
                    <li key={text} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/90">{text}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-xl border border-border bg-background/40 p-3">
                    <p className="text-xs font-semibold text-muted-foreground">{t('publicWebsite:bookDemo.sidebar.durationLabel') || (locale === 'ar' ? 'المدة' : 'Duration')}</p>
                    <p className="mt-1 text-sm font-bold text-foreground">{t('publicWebsite:bookDemo.sidebar.durationValue') || (locale === 'ar' ? '30 دقيقة' : '30 minutes')}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-background/40 p-3">
                    <p className="text-xs font-semibold text-muted-foreground">{t('publicWebsite:bookDemo.sidebar.locationLabel') || (locale === 'ar' ? 'المكان' : 'Location')}</p>
                    <p className="mt-1 text-sm font-bold text-foreground">{t('publicWebsite:bookDemo.sidebar.locationValue') || (locale === 'ar' ? 'اجتماع عبر الإنترنت' : 'Online meeting')}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-background/40 p-3">
                    <p className="text-xs font-semibold text-muted-foreground">{t('publicWebsite:bookDemo.sidebar.timezoneLabel') || (locale === 'ar' ? 'المنطقة الزمنية' : 'Timezone')}</p>
                    <p className="mt-1 text-sm font-bold text-foreground">{t('publicWebsite:bookDemo.sidebar.timezoneValue') || (locale === 'ar' ? 'آسيا/دبي' : 'Asia/Dubai')}</p>
                  </div>
                </div>
              </div>
          </aside>
        </div>
      </main>

      <LandingFooter />
    </div>
  );
}
